import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { isNil, size } from "lodash";

import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import LoanFilePreviewModal from "../../../../components/modals/files/LoanFilePreviewModal";
import {
  FilenameColumnCellRenderer,
  GroupRowInnerRenderer,
} from "../LoanDrilldownDocumentsTab/LoanDocumentManagerTable";

// Grouping is fixed to docType for this table.
const LAYOUT_FIELDS = ["docType"];

/**
 * EntityFilesTable — a stripped-down sibling of LoanDocumentManagerTable for the
 * Entity tab. It reuses the same rich filename cell (so the 3-dot "File actions"
 * menu behaves identically) but pares everything else away:
 *   - rows are grouped by Document Type, always
 *   - the file display name is the only column
 *   - no inline action icons — only the vertical 3-dot menu (`minimalActions`)
 */
const EntityFilesTable = ({ rowData }) => {
  const tableLoanId = useSelector((s) => s.loanDrilldown?._id);
  const tableLoanComments = useSelector((s) => s.loanDrilldown?.comments);

  const [previewFile, setPreviewFile] = useState(null);

  // Pre-bucket comments by loanDocument id so the filename cell's comment
  // count is an O(1) lookup (mirrors LoanDocumentManagerTable).
  const commentsByDocId = useMemo(() => {
    const map = new Map();
    for (const c of tableLoanComments || []) {
      const docIds = c?.associations?.loanDocuments;
      if (!Array.isArray(docIds)) continue;
      for (const id of docIds) {
        const list = map.get(id);
        if (list) list.push(c);
        else map.set(id, [c]);
      }
    }
    return map;
  }, [tableLoanComments]);

  const filenameCellRendererParams = useMemo(
    () => ({
      commentsByDocId,
      tableLoanId,
      layoutFields: LAYOUT_FIELDS,
      // Stripped-down presentation: 3-dot menu only, no nesting indent, and no
      // docType subline (the group row already names the type).
      minimalActions: true,
      disableLeafIndent: true,
      hideDocTypeSubline: true,
    }),
    [commentsByDocId, tableLoanId],
  );

  const columnDefs = useMemo(
    () => [
      // Grouping driver — hidden; rendered as full-width group rows below.
      // `sort: "asc"` orders the docType group rows alphabetically A→Z (without
      // it, groups follow row-encounter order — e.g. COGS ends up last).
      { field: "docType", rowGroup: true, hide: true, sort: "asc" },
      {
        colId: "file_display_name",
        headerName: "File",
        field: "file_display_name",
        flex: 1,
        minWidth: 240,
        sort: "asc",
        autoHeight: true,
        cellStyle: { cursor: "pointer", paddingTop: 8, paddingBottom: 8 },
        cellRenderer: FilenameColumnCellRenderer,
        cellRendererParams: filenameCellRendererParams,
      },
    ],
    [filenameCellRendererParams],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      cellClass: "ag-cell-vertical-center",
    }),
    [],
  );

  const groupRowRendererParams = useMemo(
    () => ({ innerRenderer: GroupRowInnerRenderer, suppressCount: true }),
    [],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const getRowHeight = useCallback((params) => {
    if (params?.node?.group) return 42;
    return 44;
  }, []);

  const onRowClicked = useCallback((params) => {
    if (params.node.group) return;
    const el = params.event?.target;
    // Don't open the preview when the click was on the 3-dot menu / an action
    // button or its popovers — those manage their own behavior.
    if (
      el instanceof Element &&
      (el.closest("button") ||
        el.closest("a") ||
        el.closest('[data-rename-ui="true"]'))
    ) {
      return;
    }
    setPreviewFile(params?.data);
  }, []);

  return (
    <>
      <AgGridTableWrapper compactTable noBorder borderRadius>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          getRowHeight={getRowHeight}
          onRowClicked={onRowClicked}
          domLayout="autoHeight"
          animateRows={false}
          groupDisplayType="groupRows"
          groupRowRendererParams={groupRowRendererParams}
          groupDefaultExpanded={-1}
          headerHeight={0}
          enableCellTextSelection
          suppressCellFocus
        />
      </AgGridTableWrapper>
      <LoanFilePreviewModal
        file={previewFile}
        relatedFiles={size(rowData) > 1 ? rowData : null}
        open={!isNil(previewFile)}
        containerId={previewFile?.containerName}
        onClose={() => setPreviewFile(null)}
        onSelectRelatedFile={setPreviewFile}
      />
    </>
  );
};

export default EntityFilesTable;
